import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Sparkles, Building2 } from 'lucide-react';

export default function AuthPage() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regCountry, setRegCountry] = useState('');
  const [regCity, setRegCity] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(loginEmail, loginPassword);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await register({
        name: regName,
        email: regEmail,
        password: regPassword,
        country: regCountry || null,
        diaspora_city: regCity || null
      });
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#050B14] flex items-center justify-center px-6 py-12 relative overflow-hidden">
      <div className="absolute inset-0 afii-grid-pattern" />
      <div className="absolute top-1/4 right-1/3 w-[400px] h-[400px] bg-[#D4A843] opacity-[0.03] blur-[150px] rounded-full" />

      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-[#D4A843] mb-4">
            <Sparkles className="w-6 h-6" />
            <span className="font-['Outfit'] font-bold text-xl">AFII</span>
          </div>
          <h1 className="font-['Outfit'] font-bold text-2xl text-white">Welcome to AFII</h1>
          <p className="font-['Manrope'] text-white/40 text-sm mt-1">Start your learning journey</p>
        </div>

        <div className="bg-[#0B1D3A]/60 backdrop-blur-xl border border-white/10 rounded-2xl p-6 shadow-xl">
          {/* Google Sign-In */}
          <button
            data-testid="google-signin-btn"
            onClick={() => {
              window.location.href = `${window.location.origin}/api/auth/google/start`;
            }}
            className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-full bg-white text-[#0B1D3A] font-['Manrope'] font-semibold text-sm hover:bg-gray-100 transition-colors duration-200 mb-5"
          >
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-white/30 text-xs font-['Manrope']">or use email</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          {error && (
            <div data-testid="auth-error" className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm font-['Manrope']">
              {error}
            </div>
          )}

          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-white/5 rounded-xl mb-1">
              <TabsTrigger data-testid="login-tab" value="login" className="rounded-lg text-white/60 data-[state=active]:bg-[#D4A843] data-[state=active]:text-[#0B1D3A] font-['Manrope'] text-sm">
                Sign In
              </TabsTrigger>
              <TabsTrigger data-testid="register-tab" value="register" className="rounded-lg text-white/60 data-[state=active]:bg-[#D4A843] data-[state=active]:text-[#0B1D3A] font-['Manrope'] text-sm">
                Register
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4 mt-4">
                <div>
                  <Label className="text-white/60 text-sm font-['Manrope']">Email</Label>
                  <Input
                    data-testid="login-email-input"
                    type="email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/25 focus:border-[#D4A843] focus:ring-1 focus:ring-[#D4A843] mt-1.5 rounded-xl"
                    placeholder="your@email.com"
                    required
                  />
                </div>
                <div>
                  <Label className="text-white/60 text-sm font-['Manrope']">Password</Label>
                  <Input
                    data-testid="login-password-input"
                    type="password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/25 focus:border-[#D4A843] focus:ring-1 focus:ring-[#D4A843] mt-1.5 rounded-xl"
                    placeholder="Enter password"
                    required
                  />
                </div>
                <Button
                  data-testid="login-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#D4A843] text-[#0B1D3A] font-bold hover:bg-[#F5D78E] rounded-full py-5 mt-2"
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4 mt-4">
                <div>
                  <Label className="text-white/60 text-sm font-['Manrope']">Full Name</Label>
                  <Input
                    data-testid="register-name-input"
                    value={regName}
                    onChange={(e) => setRegName(e.target.value)}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/25 focus:border-[#D4A843] focus:ring-1 focus:ring-[#D4A843] mt-1.5 rounded-xl"
                    placeholder="Your name"
                    required
                  />
                </div>
                <div>
                  <Label className="text-white/60 text-sm font-['Manrope']">Email</Label>
                  <Input
                    data-testid="register-email-input"
                    type="email"
                    value={regEmail}
                    onChange={(e) => setRegEmail(e.target.value)}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/25 focus:border-[#D4A843] focus:ring-1 focus:ring-[#D4A843] mt-1.5 rounded-xl"
                    placeholder="your@email.com"
                    required
                  />
                </div>
                <div>
                  <Label className="text-white/60 text-sm font-['Manrope']">Password</Label>
                  <Input
                    data-testid="register-password-input"
                    type="password"
                    value={regPassword}
                    onChange={(e) => setRegPassword(e.target.value)}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/25 focus:border-[#D4A843] focus:ring-1 focus:ring-[#D4A843] mt-1.5 rounded-xl"
                    placeholder="Create password (6+ chars)"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-white/60 text-sm font-['Manrope']">Country</Label>
                    <Input
                      data-testid="register-country-input"
                      value={regCountry}
                      onChange={(e) => setRegCountry(e.target.value)}
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/25 focus:border-[#D4A843] focus:ring-1 focus:ring-[#D4A843] mt-1.5 rounded-xl"
                      placeholder="Optional"
                    />
                  </div>
                  <div>
                    <Label className="text-white/60 text-sm font-['Manrope']">Diaspora City</Label>
                    <Input
                      data-testid="register-city-input"
                      value={regCity}
                      onChange={(e) => setRegCity(e.target.value)}
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/25 focus:border-[#D4A843] focus:ring-1 focus:ring-[#D4A843] mt-1.5 rounded-xl"
                      placeholder="Optional"
                    />
                  </div>
                </div>
                <Button
                  data-testid="register-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#D4A843] text-[#0B1D3A] font-bold hover:bg-[#F5D78E] rounded-full py-5 mt-2"
                >
                  {loading ? 'Creating account...' : 'Create Account'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <div className="mt-5 pt-4 border-t border-white/[0.06] text-center">
            <a href="/auth/employer" className="inline-flex items-center gap-2 font-['Manrope'] text-white/30 text-xs hover:text-white/50 transition-opacity">
              <Building2 className="w-3.5 h-3.5" />
              I'm an Employer / Recruiter
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
